import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { showPinnedPopover } from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import { setSelectedLocation } from '@rsos/reduxDataStore/src/features/gisSearch/gisSearchSlice';
import { fetchWhat3wordsLocation } from '@rsos/reduxDataStore/src/features/what3words/what3wordsAPI';

interface What3WordsApiResponse {
  coordinates?: { lat: number; lng: number };
  words?: string;
  nearestPlace?: string;
  country?: string;
}

const fetchW3W = fetchWhat3wordsLocation as unknown as (
  w3w: string,
) => Promise<What3WordsApiResponse>;

/**
 * Strict `word.word.word` shape: exactly two periods between three
 * letters-only tokens, no whitespace inside the phrase. The leading
 * `///` is optional and may be followed by whitespace, but the phrase
 * itself must be unambiguous.
 *
 * Why so strict? The legacy `w3wInputHelper` treats spaces and dots
 * as interchangeable separators, so terms like `"chuck e cheese"` or
 * `"chuck e. cheese"` get hijacked into a what3words lookup before
 * the regular search ever runs — masking real place / parcel /
 * caller-name matches behind a misfiring W3W shortcut. Requiring two
 * literal periods makes the intent explicit.
 */
const W3W_STRICT_PATTERN = /^(?:\/\/\/)?\s*([a-z]+\.[a-z]+\.[a-z]+)$/i;

/**
 * Parse a search term as a what3words address. Returns the normalized
 * lowercase `word.word.word` string, or `null` if the term doesn't
 * match the strict shape described on `W3W_STRICT_PATTERN`.
 */
export function parseWhat3WordsTerm(term: string): string | null {
  const trimmed = term.trim();
  if (!trimmed) return null;
  const match = W3W_STRICT_PATTERN.exec(trimmed);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Build a `gisSearch.selectedLocation`-shaped object from a W3W lookup
 * result. The pinned popover and GisSearchGL layer both consume this
 * shape ({ location: { x, y }, attributes: { ... } }) so the W3W hit
 * renders identically to a regular place result.
 */
function toSelectedLocation(w3w: string, response: What3WordsApiResponse) {
  const lat = response.coordinates?.lat;
  const lng = response.coordinates?.lng;
  if (lat == null || lng == null) return null;
  const placeName = `///${response.words || w3w}`;
  const nearest = response.nearestPlace || '';
  return {
    location: { x: lng, y: lat },
    attributes: {
      PlaceName: placeName,
      StAddr: nearest,
      SubAddr: '',
      City: '',
      RegionAbbr: '',
      Postal: '',
      MetroArea: '',
      Region: '',
      Zone: '',
      Country: response.country || '',
      LongLabel: nearest ? `${placeName} · ${nearest}` : placeName,
      Addr_type: 'what3words',
      Loc_name: 'what3words',
    },
  };
}

interface UseWhat3WordsSearch {
  /** See {@link parseWhat3WordsTerm}. */
  parse: (term: string) => string | null;
  /**
   * Look up the given W3W phrase and pin its location on the map using
   * the standard gisSearch layer + pinned popover. Resolves once the
   * marker has been dispatched (or rejects silently on lookup failure).
   */
  search: (w3w: string) => Promise<void>;
}

/**
 * Thin redux-aware wrapper that translates a what3words phrase into a
 * gisSearch selection. Used by GlobalMapSearch to keep its
 * `triggerSearch` path readable and to colocate all W3W logic in one
 * file.
 */
export default function useWhat3WordsSearch(): UseWhat3WordsSearch {
  const dispatch = useDispatch();

  const search = useCallback(
    async (w3w: string) => {
      try {
        const response = await fetchW3W(w3w);
        const loc = toSelectedLocation(w3w, response);
        if (!loc) return;
        dispatch(setSelectedLocation(loc));
        dispatch(showPinnedPopover({ loc }));
      } catch {
        // Silently swallow: a bad W3W phrase just produces no marker,
        // matching the "no results" behavior of the regular search.
      }
    },
    [dispatch],
  );

  return useMemo(() => ({ parse: parseWhat3WordsTerm, search }), [search]);
}
